/**
 * An entity's labelled descriptions (the default one plus any extras), with
 * the mutations the entity page's inline table needs. Mirrors what
 * the old Edit modal did in one batch, but one call per change.
 *
 * Only fetches when `enabled` (Entity.jsx passes "loaded and not a
 * federation-cached copy"); the extra rows themselves are editor-only.
 *
 *   const { descriptions, add, update, remove, setDefault } = useEntityDescriptions(entityId, enabled);
 */

import { useState, useEffect, useCallback } from 'react';
import { useApi } from './use_api';

export function useEntityDescriptions(entityId, enabled) {
    const api = useApi();
    const [descriptions, setDescriptions] = useState([]);

    const refresh = useCallback(() => {
        if (!enabled || !entityId) { setDescriptions([]); return Promise.resolve(); }
        return api.get(`/api/entities/${entityId}/descriptions`)
            .then(data => setDescriptions(Array.isArray(data) ? data : []))
            .catch(() => setDescriptions([]));
    }, [api, entityId, enabled]);

    useEffect(() => { refresh(); }, [refresh]);

    const add = useCallback((label, content) => api.post(
        `/api/entities/${entityId}/descriptions`,
        { entity_id: Number(entityId), label, content, is_default: false },
    ).then(refresh), [api, entityId, refresh]);

    const update = useCallback((id, patch) => api.put(`/api/entities/descriptions/${id}`, patch)
        .then(refresh), [api, refresh]);

    const remove = useCallback(id => api.del(`/api/entities/descriptions/${id}`)
        .then(refresh), [api, refresh]);

    const setDefault = useCallback(id => api.post(`/api/entities/descriptions/${id}/set-default`)
        .then(refresh), [api, refresh]);

    return { descriptions, refresh, add, update, remove, setDefault };
}

export default useEntityDescriptions;
